import VueRouter, {
  isNavigationFailure,
  RawLocation,
  Route,
} from 'vue-router';

type OnComplete = (route: Route) => void;
type OnAbort = (err: Error) => void;

type PromiseForm = (to: RawLocation) => Promise<Route>;
type CallbackForm = (to: RawLocation, onComplete?: OnComplete, onAbort?: OnAbort) => void;

type RouterNavigate = typeof VueRouter.prototype.push | typeof VueRouter.prototype.replace;

// Do not allow more calls
let patched = false;

function wrap(original: RouterNavigate) {
  return function silenced(
    this: VueRouter,
    location: RawLocation,
    onComplete?: OnComplete,
    onAbort?: OnAbort
  ): Promise<Route | void> | void {
    if (onComplete || onAbort) {
      return (original as CallbackForm).call(this, location, onComplete, onAbort);
    }
    return (original as PromiseForm).call(this, location).catch((err: unknown) => {
      if (!isNavigationFailure(err)) throw err;
    });
  };
}

/**
 * Patches `VueRouter.prototype.push`/`.replace` so the promise silently
 * ignores NavigationFailure (redirected/aborted/cancelled/duplicated). Real
 * errors still reject. Callback form (`push(to, onComplete, onAbort)`) is
 * passed through unchanged so callers that opt into raw behavior keep it.
 *
 * Idempotent — call once at app bootstrap, before any `new VueRouter(...)`.
 */
export default function swallowNavigationErrorPatch(): void {
  if (patched) return;
  patched = true;

  VueRouter.prototype.push = wrap(VueRouter.prototype.push) as RouterNavigate;
  VueRouter.prototype.replace = wrap(VueRouter.prototype.replace) as RouterNavigate;
}
